import {
  GridSpaceType,
  LevelType,
  LevelTypeType,
  PositionAndDimensionsType,
  PositionType
} from 'data/types';
import { getSpacePolygonVerticesFeet } from 'utils/3d-polygon';
import { boundingBoxOfVertices, getSpaceVertices } from 'utils/space-polygon';

export const LEVEL_HEIGHTS: Record<LevelTypeType, number> = {
  level: 2.5,
  basement: 2.5,
  crawlspace: 1,
  attic: 2.5
};

export const SCALE = 0.5;

export type LevelYOffsetInfo = {
  levelIndex: number;
  yOffset: number;
  height: number;
  levelType: LevelTypeType;
};

export function getLevelHeight(levelType: LevelTypeType): number {
  return LEVEL_HEIGHTS[levelType] ?? 2.5;
}

export function getGroundLevelIndex(structure: LevelType[]): number {
  const index = structure.findIndex(
    (level) => level.type !== 'basement' && level.type !== 'crawlspace'
  );
  return Math.max(index, 0);
}

export function calculateLevelYOffsets(
  structure: LevelType[]
): LevelYOffsetInfo[] {
  const groundLevelIndex = getGroundLevelIndex(structure);
  const offsets: LevelYOffsetInfo[] = [];

  let belowGroundOffset = 0;
  for (let i = groundLevelIndex - 1; i >= 0; i -= 1) {
    const level = structure[i];
    const height = getLevelHeight(level.type);
    belowGroundOffset -= height;
    offsets[i] = {
      levelIndex: i,
      yOffset: belowGroundOffset,
      height,
      levelType: level.type
    };
  }

  let aboveGroundOffset = 0;
  for (let i = groundLevelIndex; i < structure.length; i += 1) {
    const level = structure[i];
    const height = getLevelHeight(level.type);
    offsets[i] = {
      levelIndex: i,
      yOffset: aboveGroundOffset,
      height,
      levelType: level.type
    };
    aboveGroundOffset += height;
  }

  return offsets;
}

export function getLevelIndexForSpace(
  spaceId: string,
  structure: LevelType[]
): number {
  for (let levelIndex = 0; levelIndex < structure.length; levelIndex += 1) {
    const level = structure[levelIndex];
    for (const section of level.sections) {
      for (const space of section.spaces) {
        if (space.uniqueId === spaceId) {
          return levelIndex;
        }
      }
    }
  }
  return 0;
}

export type SpaceBounds = {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
};

export type CenterOffset3D = {
  offsetX: number;
  offsetZ: number;
};

export function getSpace3DGridPosition(space: {
  position?: { x: number; y: number };
  width?: number;
  length?: number;
  vertices?: PositionType[];
}): PositionAndDimensionsType {
  const bbox = boundingBoxOfVertices(getSpaceVertices(space));
  return {
    x: space.position?.x ?? 0,
    y: space.position?.y ?? 0,
    width: bbox.width,
    height: bbox.height
  };
}

export function getSpaceBoundsFeet(
  space: Pick<GridSpaceType, 'position' | 'vertices'>
): SpaceBounds {
  const vertices = getSpacePolygonVerticesFeet(
    space.vertices,
    space.position.width,
    space.position.height
  );
  const bbox = boundingBoxOfVertices(vertices);
  return {
    minX: space.position.x + bbox.minX,
    maxX: space.position.x + bbox.minX + bbox.width,
    minZ: space.position.y + bbox.minY,
    maxZ: space.position.y + bbox.minY + bbox.height
  };
}

export function getItemsCenterOffset3D(
  items: Array<Pick<GridSpaceType, 'position' | 'vertices'>>,
  scale: number = SCALE
): CenterOffset3D {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  for (const item of items) {
    const bounds = getSpaceBoundsFeet(item);
    if (bounds.maxX > bounds.minX || bounds.maxZ > bounds.minZ) {
      minX = Math.min(minX, bounds.minX * scale);
      maxX = Math.max(maxX, bounds.maxX * scale);
      minZ = Math.min(minZ, bounds.minZ * scale);
      maxZ = Math.max(maxZ, bounds.maxZ * scale);
    }
  }

  if (!Number.isFinite(minX)) {
    return { offsetX: 0, offsetZ: 0 };
  }

  return {
    offsetX: -(minX + maxX) / 2,
    offsetZ: -(minZ + maxZ) / 2
  };
}

export function getStructureTargetY(structure: LevelType[]): number {
  const offsets = calculateLevelYOffsets(structure);
  if (offsets.length === 0) {
    return 0;
  }

  let minY = Infinity;
  let maxY = -Infinity;
  for (const info of offsets) {
    minY = Math.min(minY, info.yOffset);
    maxY = Math.max(maxY, info.yOffset + info.height);
  }

  if (!Number.isFinite(minY)) {
    return 0;
  }

  return (minY + maxY) / 2;
}

export function getBasementSpaceBounds(
  spaces: GridSpaceType[],
  structure: LevelType[],
  offsetX: number,
  offsetZ: number
): SpaceBounds[] {
  const basementBounds: SpaceBounds[] = [];

  for (const space of spaces) {
    const levelIndex = getLevelIndexForSpace(space.id, structure);
    const level = structure[levelIndex];

    if (level?.type === 'basement') {
      const bounds = getSpaceBoundsFeet(space);
      basementBounds.push({
        minX: bounds.minX * SCALE + offsetX,
        maxX: bounds.maxX * SCALE + offsetX,
        minZ: bounds.minZ * SCALE + offsetZ,
        maxZ: bounds.maxZ * SCALE + offsetZ
      });
    }
  }

  return basementBounds;
}
